import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";

import CustomButton from "../Button";
import { customDays, customHours } from "../../Constants";
import {
  calculateAmount,
  convertTimeTo12h,
  convertToMinutes,
  formatPrice,
  showToast,
} from "../../utils/helpers";
import { API } from "../../utils/Apis";

interface EditBookingModalType {
  show: boolean;
  booking: any;
  onClose: () => void;
  onUpdated: (updated: any) => void;
}

// Derive the matching "timeFrame" dropdown value (e.g. "3 hours" / "2 days")
// from a booking's existing start/end, so the form pre-fills the right option.
const deriveTimeFrame = (booking: any): string => {
  const start = moment(
    `${booking?.startDate} ${booking?.startTime}`,
    "YYYY-MM-DD HH:mm"
  );
  const end = moment(
    `${booking?.endDate} ${booking?.endTime}`,
    "YYYY-MM-DD HH:mm"
  );

  if (!start.isValid() || !end.isValid()) {
    return booking?.type === "custom"
      ? customDays[0].value
      : customHours[0].value;
  }

  if (booking?.type === "custom") {
    const days = Math.max(1, Math.round(end.diff(start, "days", true)));
    return `${days} ${days === 1 ? "day" : "days"}`;
  }

  const hours = Math.max(1, Math.round(end.diff(start, "hours", true)));
  return `${hours} ${hours === 1 ? "hour" : "hours"}`;
};

const EditBookingModal: React.FC<EditBookingModalType> = ({
  show,
  booking,
  onClose,
  onUpdated,
}) => {
  const [loading, setLoading] = useState(false);

  const [bookingData, setBookingData] = useState({
    type: "normal",
    startDate: moment().format("YYYY-MM-DD"),
    startTime: moment().format("HH:mm"),
    endDate: "",
    endTime: "",
    arrivalTime: "",
    timeFrame: customHours[0].value,
    offeredAmount: "",
  });

  const [slots, setSlots] = useState<{ start: string; end: string }[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Whether the user has manually edited the offered amount. Until they do, it
  // tracks the calculated price as dates/times change.
  const [offeredTouched, setOfferedTouched] = useState(false);

  const spaceId = booking?.spaceId || booking?.space?.id || "";
  const ratePerHour = booking?.space?.ratePerHour || "";

  // Only offer short-term durations that meet the space's minimum.
  const availableHours = useMemo(() => {
    const minMinutes = (Number(booking?.space?.minHours) || 0) * 60;
    const filtered = customHours.filter(
      (h) => convertToMinutes(h.value) >= minMinutes
    );
    return filtered.length > 0 ? filtered : customHours;
  }, [booking?.space?.minHours]);

  // Pre-fill the form from the booking being edited.
  useEffect(() => {
    if (booking) {
      setBookingData({
        type: booking.type || "normal",
        startDate: booking.startDate || moment().format("YYYY-MM-DD"),
        startTime: booking.startTime || moment().format("HH:mm"),
        endDate: booking.endDate || "",
        endTime: booking.endTime || "",
        // For short-term, the existing start time is the selected arrival slot.
        arrivalTime:
          booking.type === "custom" ? "" : booking.startTime || "",
        timeFrame: deriveTimeFrame(booking),
        offeredAmount:
          booking.offeredAmount !== null && booking.offeredAmount !== undefined
            ? String(booking.offeredAmount)
            : "",
      });

      // Preserve an existing offer; only auto-sync when there's none yet.
      setOfferedTouched(
        booking.offeredAmount !== null && booking.offeredAmount !== undefined
      );
    }
  }, [booking]);

  const onChange = (field: string, value: string) => {
    setBookingData((prev) => ({ ...prev, [field]: value }));
  };

  // Fetch arrival-time slots for short-term bookings.
  const getSlots = useCallback(() => {
    if (bookingData.type !== "normal" || !spaceId || !bookingData.startDate) {
      setSlots([]);
      return;
    }

    const duration = convertToMinutes(bookingData.timeFrame);
    const date = moment(bookingData.startDate).format("YYYY-MM-DD");

    setLoadingSlots(true);
    API.getDateSlots({ spaceId, date, duration })
      .then((res) => {
        if (
          res.type === "success" &&
          res.data &&
          Array.isArray(res.data.slots)
        ) {
          setSlots(res.data.slots);
        } else {
          setSlots([]);
        }
      })
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [spaceId, bookingData.type, bookingData.timeFrame, bookingData.startDate]);

  useEffect(() => {
    getSlots();
  }, [getSlots]);

  const effectiveStartTime =
    bookingData.type === "normal"
      ? bookingData.arrivalTime
      : bookingData.startTime;

  // Build the start moment from the selected date + effective start time.
  const buildStartMoment = () =>
    moment(bookingData.startDate).set({
      hour: moment(effectiveStartTime, ["hh:mmA", "hh:mm A", "HH:mm"]).hour(),
      minute: moment(effectiveStartTime, [
        "hh:mmA",
        "hh:mm A",
        "HH:mm",
      ]).minute(),
      second: 0,
      millisecond: 0,
    });

  // Short-term: end derived from the chosen duration. Long-term: explicit end
  // date + time entered by the user.
  const buildEndMoment = (startMoment: moment.Moment) => {
    if (bookingData.type === "custom") {
      if (!bookingData.endDate || !bookingData.endTime) return null;
      return moment(bookingData.endDate).set({
        hour: moment(bookingData.endTime, ["hh:mmA", "hh:mm A", "HH:mm"]).hour(),
        minute: moment(bookingData.endTime, [
          "hh:mmA",
          "hh:mm A",
          "HH:mm",
        ]).minute(),
        second: 0,
        millisecond: 0,
      });
    }
    const temp = String(bookingData.timeFrame).split(" ");
    return startMoment.clone().add(Number(temp[0]), temp[1] as any);
  };

  // Live price preview for the currently selected dates/times. The server
  // recomputes authoritatively on submit; this is only a display aid.
  const previewAmount = (() => {
    try {
      if (!effectiveStartTime) return 0;

      const startDate = buildStartMoment();
      const endDate = buildEndMoment(startDate);
      if (!endDate || endDate.isSameOrBefore(startDate)) return 0;

      return calculateAmount(
        ratePerHour,
        startDate.format("YYYY-MM-DD"),
        startDate.format("HH:mm"),
        endDate.format("YYYY-MM-DD"),
        endDate.format("HH:mm")
      );
    } catch {
      return 0;
    }
  })();

  // Keep the offered amount in sync with the calculated price for long-term
  // bookings until the user manually edits it.
  useEffect(() => {
    if (
      bookingData.type === "custom" &&
      !offeredTouched &&
      previewAmount > 0
    ) {
      setBookingData((prev) => ({
        ...prev,
        offeredAmount: String(previewAmount),
      }));
    }
  }, [bookingData.type, offeredTouched, previewAmount]);

  const submitUpdate = () => {
    if (!bookingData.startDate) {
      showToast("error", "Please select a date.");
      return;
    }
    if (!effectiveStartTime) {
      showToast(
        "error",
        bookingData.type === "normal"
          ? "Please select an arrival time."
          : "Please select a start time."
      );
      return;
    }
    if (
      bookingData.type === "custom" &&
      (!bookingData.endDate || !bookingData.endTime)
    ) {
      showToast("error", "Please select an end date and time.");
      return;
    }
    if (loading) {
      return;
    }

    // Enforce the space's minimum booking length for short-term bookings.
    const minHours = Number(booking?.space?.minHours) || 0;
    if (
      bookingData.type === "normal" &&
      minHours > 0 &&
      convertToMinutes(bookingData.timeFrame) < minHours * 60
    ) {
      showToast(
        "error",
        `This space has a minimum booking of ${minHours} hour${
          minHours > 1 ? "s" : ""
        }.`
      );
      return;
    }

    const startDate = buildStartMoment();
    const endDate = buildEndMoment(startDate);

    if (!endDate || endDate.isSameOrBefore(startDate)) {
      showToast("error", "The end time must be after the start time.");
      return;
    }

    // Long-term bookings must span at least 24 hours.
    if (
      bookingData.type === "custom" &&
      endDate.diff(startDate, "minutes") < 1440
    ) {
      showToast("error", "A long-term booking must be at least 24 hours.");
      return;
    }

    const startDateT = startDate.format("YYYY-MM-DD");
    const endDateT = endDate.format("YYYY-MM-DD");
    const startTimeT = startDate.format("HH:mm");
    const endTimeT = endDate.format("HH:mm");

    const data: any = {
      id: booking.id,
      venueId: booking.venueId,
      type: bookingData.type,
      day: startDate.format("ddd"),
      startDate: startDateT,
      startTime: startTimeT,
      endDate: endDateT,
      endTime: endTimeT,
    };

    if (bookingData.type === "custom") {
      const offer =
        bookingData.offeredAmount !== "" &&
        !Number.isNaN(Number(bookingData.offeredAmount))
          ? Number(bookingData.offeredAmount)
          : previewAmount;

      if (!offer || offer <= 0) {
        showToast("error", "Please enter an offered amount greater than $0.");
        return;
      }
      if (previewAmount && offer > previewAmount) {
        showToast(
          "error",
          "Your offered amount can't be more than the calculated price."
        );
        return;
      }
      data.offeredAmount = offer;
    }

    setLoading(true);
    API.updateBooking(data)
      .then((res) => {
        if (res.type === "success") {
          showToast("success", res.message || "Your booking has been updated.");
          onUpdated(res.data);
          onClose();
        } else {
          showToast("error", res.message);
        }
      })
      .catch((err) => {
        showToast("error", err.message);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal show={show} onHide={() => onClose()} className="edit-booking-modal">
      <Modal.Header closeButton>Edit Booking</Modal.Header>
      <Modal.Body>
        <div className="booking-form-wrapper">
          <form>
            <div className="form-group">
              <label className="d-block">Booking Type</label>

              <div className="booking-type-options">
                <Form.Check
                  inline
                  id="edit-booking-type-normal"
                  type="radio"
                  label="Short Term"
                  checked={bookingData.type === "normal"}
                  onChange={() => {
                    setOfferedTouched(false);
                    setBookingData((prev) => ({
                      ...prev,
                      type: "normal",
                      timeFrame: availableHours[0].value,
                      arrivalTime: "",
                    }));
                  }}
                />

                <Form.Check
                  inline
                  id="edit-booking-type-custom"
                  type="radio"
                  label="Long Term"
                  checked={bookingData.type === "custom"}
                  onChange={() =>
                    setBookingData((prev) => {
                      // Long-term must span at least 24 hours; default the end
                      // to start + 1 day if not already set to a valid span.
                      const startD =
                        prev.startDate || moment().format("YYYY-MM-DD");
                      const startT =
                        prev.startTime || moment().format("HH:mm");
                      return {
                        ...prev,
                        type: "custom",
                        timeFrame: customDays[0].value,
                        endDate:
                          prev.endDate ||
                          moment(startD).add(1, "day").format("YYYY-MM-DD"),
                        endTime: prev.endTime || startT,
                      };
                    })
                  }
                />
              </div>
            </div>

            {/* Short-term: duration + date. */}
            {bookingData.type === "normal" && (
              <div className="row">
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label>Time Block</label>
                    <div className="custom-select">
                      <select
                        name="timeFrame"
                        className="form-control"
                        onChange={(e) => onChange("timeFrame", e.target.value)}
                        value={bookingData.timeFrame}
                      >
                        {availableHours.map((item, index) => (
                          <option
                            value={item.value}
                            key={`edit-time-block-${item.value}-${index}`}
                          >
                            {item.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label>Event Date</label>
                    <div className="with-icon">
                      <DatePicker
                        showIcon
                        icon="far fa-calendar"
                        value={
                          bookingData.startDate
                            ? moment(bookingData.startDate).format("MM/DD/YYYY")
                            : ""
                        }
                        selected={
                          bookingData.startDate
                            ? new Date(bookingData.startDate)
                            : null
                        }
                        minDate={new Date()}
                        className="form-control"
                        placeholderText="MM/DD/YYYY"
                        onChange={(date: Date | null) =>
                          onChange(
                            "startDate",
                            date ? moment(date).format("YYYY-MM-DD") : ""
                          )
                        }
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Long-term: explicit start/end dates and times. */}
            {bookingData.type === "custom" && (
              <div className="row">
                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label>Start Date</label>
                    <div className="with-icon">
                      <DatePicker
                        showIcon
                        icon="far fa-calendar"
                        value={
                          bookingData.startDate
                            ? moment(bookingData.startDate).format("MM/DD/YYYY")
                            : ""
                        }
                        selected={
                          bookingData.startDate
                            ? new Date(bookingData.startDate)
                            : null
                        }
                        minDate={new Date()}
                        className="form-control"
                        placeholderText="MM/DD/YYYY"
                        onChange={(date: Date | null) =>
                          onChange(
                            "startDate",
                            date ? moment(date).format("YYYY-MM-DD") : ""
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label>Start Time</label>
                    <input
                      type="time"
                      name="startTime"
                      placeholder="Select Time"
                      className="form-control"
                      value={bookingData.startTime}
                      onChange={(e) => onChange("startTime", e.target.value)}
                    />
                  </div>
                </div>

                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label>End Date</label>
                    <div className="with-icon">
                      <DatePicker
                        showIcon
                        icon="far fa-calendar"
                        value={
                          bookingData.endDate
                            ? moment(bookingData.endDate).format("MM/DD/YYYY")
                            : ""
                        }
                        selected={
                          bookingData.endDate
                            ? new Date(bookingData.endDate)
                            : null
                        }
                        minDate={
                          bookingData.startDate
                            ? new Date(bookingData.startDate)
                            : new Date()
                        }
                        className="form-control"
                        placeholderText="MM/DD/YYYY"
                        onChange={(date: Date | null) =>
                          onChange(
                            "endDate",
                            date ? moment(date).format("YYYY-MM-DD") : ""
                          )
                        }
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-6 col-sm-12">
                  <div className="form-group">
                    <label>End Time</label>
                    <input
                      type="time"
                      name="endTime"
                      placeholder="Select Time"
                      className="form-control"
                      value={bookingData.endTime}
                      onChange={(e) => onChange("endTime", e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {bookingData.type === "normal" && (
              <div className="time-blocks-wrapper">
                <div className="time-blocks-heading">
                  <span className="label">Arrival Time</span>
                  <span className="increments">
                    {bookingData.timeFrame} increments
                  </span>
                </div>

                <div className="time-blocks">
                  {loadingSlots && (
                    <p className="time-blocks-msg">
                      <FontAwesomeIcon icon={faSpinner} spin />
                    </p>
                  )}

                  {!loadingSlots && slots.length === 0 && (
                    <p className="time-blocks-msg">
                      No time blocks available for the selected date and
                      duration.
                    </p>
                  )}

                  {!loadingSlots && slots.length > 0 && (
                    <div className="time-blocks-grid">
                      {slots.map((slot, index) => (
                        <div
                          key={`edit-arrival-${slot.start}-${index}`}
                          className={`arrival-time-item ${
                            bookingData.arrivalTime === slot.start
                              ? "active"
                              : ""
                          }`}
                          onClick={() => onChange("arrivalTime", slot.start)}
                        >
                          {convertTimeTo12h(slot.start)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {bookingData.type === "custom" && (
              <div className="form-group">
                <label>Offered Amount</label>
                <div className="offered-amount-input">
                  <span className="currency">$</span>
                  <input
                    type="number"
                    min={1}
                    step="0.01"
                    name="offeredAmount"
                    placeholder="0.00"
                    className="form-control"
                    value={bookingData.offeredAmount}
                    onChange={(e) => {
                      setOfferedTouched(true);
                      onChange("offeredAmount", e.target.value);
                    }}
                  />
                </div>
              </div>
            )}

            {ratePerHour !== "" && (
              <div className="edit-booking-amount">
                <span>Estimated Amount</span>
                <strong>${formatPrice(previewAmount)}</strong>
              </div>
            )}

            <CustomButton
              isLoading={loading}
              title="Update Booking"
              onClick={() => submitUpdate()}
            />
          </form>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default EditBookingModal;
